import { Dispatch, SetStateAction, useContext, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnlock } from "@fortawesome/pro-light-svg-icons";
import Logo from "../../components/Logo";
import { pinwheelPalette } from "@/styles/theme.ts";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { PaymentStatus } from "@/openapi/models/payment-status.ts";
import { PaymentStatusResponse } from "@/openapi";
import LoadingButton from "@mui/lab/LoadingButton";
import { APIContext } from "../../context/api";
import { handleApiError } from "@/util/error-handlers.ts";
import { useAlert } from "@/hooks/useAlert";
import { useTranslation } from "react-i18next";

interface Props {
  paymentStatusResponse: PaymentStatusResponse | undefined;
  setPaymentStatusResponse: Dispatch<SetStateAction<PaymentStatusResponse | undefined>>;
}

export const UnpaidSubscription = ({ paymentStatusResponse, setPaymentStatusResponse }: Props) => {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const alert = useAlert();
  const { getCaregiverService } = useContext(APIContext);
  const { t } = useTranslation("settings");

  const getPaymentStatus = async () => {
    try {
      const caregiverService = await getCaregiverService();
      return (await caregiverService.getSubscriptionPaymentStatus()).data;
    } catch(error) {
      handleApiError(error);
    }
  };

  const handleUnlock = async () => {
    setLoading(true);
    const paymentStatusCheckResult = await getPaymentStatus();
    setPaymentStatusResponse(paymentStatusCheckResult);
    setLoading(false);
    if (paymentStatusCheckResult && paymentStatusCheckResult.paymentStatus === PaymentStatus.UnpaidSubscription) {
      alert.error(t("unlockAccountUpdatePaymentMethod"));
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid size={{ xs: 11, sm: 9, md: 8, lg: 6 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <Logo />
          </Box>
          <Typography variant={isXs ? "h4" : "h3"} component="h1" sx={{ mb: 2 }}>
            {t("updateYourPaymentMethod")}
          </Typography>
          <Typography sx={{ color: pinwheelPalette.grey[50], mb: 3 }}>
            {t("looksSubscriptionPaymentFailed")}
          </Typography>
        </Box>
        <Box className="pageMainContent">
          {paymentStatusResponse?.invoiceUrl && (
            <>
              <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {t("instructions")}
                </Typography>
                <Box sx={{ ml: 3 }}>
                  <ol>
                    <li>
                      <a
                        style={{ textDecoration: "underline", color: pinwheelPalette.purple[100] }}
                        href={paymentStatusResponse.invoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("useThisLink")}
                      </a>{" "}
                      {t("toUpdatePaymentMethod")}
                    </li>
                    <li>{t("onceUpdatedPaymentMethodTapUnlockMyAccount")}</li>
                  </ol>
                </Box>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                {loading ? (
                  <LoadingButton loading={loading} loadingPosition="start" startIcon={<></>} variant="contained" sx={{ pl: 5 }}>
                    <span>{t("unlockMyAccount")}</span>
                  </LoadingButton>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faUnlock} className="fa-icons" />}
                    sx={{
                      color: pinwheelPalette.red[100],
                      borderColor: pinwheelPalette.red[100],
                      "&:hover": { borderColor: pinwheelPalette.red[100] }
                    }}
                    onClick={() => handleUnlock()}
                  >
                    {t("unlockMyAccount")}
                  </Button>
                )}
              </Box>
            </>
          )}

          {!paymentStatusResponse?.invoiceUrl && (
            <>
              <Alert severity="warning" sx={{ alignItems: "center" }}>
                {t("unableToGeneratePaymentLink")}{" "}
                <a href="https://support.pinwheel.com/hc/en-us" target="_blank" rel="noreferrer">
                  {t("liveChat", { ns: "common" })}
                </a>{" "}
                {t("or", { ns: "common" })} <a href="tel:8889037977">{"(888) 903-7977"}</a>.
              </Alert>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
